import { getDefaultAvatar } from "@repo/fika-design-system/avatar"
import { cdnClass } from "@repo/fika-design-system/cdn-class"
import { createMemo, For, Show } from "solid-js"
import { graphql, type ResultOf } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { displayName as getDisplayName } from "@/lib/display-name"
import {
  avatarClass,
  avatarOverflowClass,
  avatarStackClass,
  participantInfoColumnClass,
  participantPublicationUrlClass,
  participantsNameClass,
  participantsRowClass,
} from "./_thread-participants.css"

const MAX_VISIBLE_AVATARS = 3
const MAX_VISIBLE_NAMES = 3

export const ThreadParticipantsFragment = graphql(`
  fragment ThreadParticipantsFragment on Thread {
    participants {
      id
      isMe
      authorName
      title
      username
      email
      profileImage
      publicationUrl
    }
  }
`)

type Participant = ResultOf<
  typeof ThreadParticipantsFragment
>["participants"][number]

export function ThreadParticipants(props: {
  participants: ReadonlyArray<Participant>
  individual?: boolean
}) {
  const { LL } = useI18nContext()

  const visibleParticipants = createMemo(() => {
    const others = props.participants.filter(p => !p.isMe)
    return others.length > 0 ? others : props.participants
  })

  const participantNames = createMemo(() => {
    const names = visibleParticipants().map(p =>
      p.isMe
        ? LL().inbox.you()
        : (getDisplayName(p) ?? LL().comments.anonymousParticipant()),
    )
    if (names.length <= MAX_VISIBLE_NAMES) return names.join(", ")
    const visible = names.slice(0, MAX_VISIBLE_NAMES).join(", ")
    const remaining = names.length - MAX_VISIBLE_NAMES
    return `${visible} ${LL().inbox.andMore({ count: remaining })}`
  })

  const overflowCount = createMemo(() => {
    const count = visibleParticipants().length - MAX_VISIBLE_AVATARS
    return count > 0 ? count : 0
  })

  if (props.individual) {
    return (
      <div class={participantsRowClass}>
        <For each={props.participants}>
          {participant => (
            <>
              <img
                src={
                  participant.profileImage
                    ? cdnClass(participant.profileImage, "smallThumb")
                    : getDefaultAvatar(participant.id)
                }
                alt={getDisplayName(participant) ?? ""}
                class={avatarClass}
              />
              <div class={participantInfoColumnClass}>
                <span class={participantsNameClass}>
                  {participant.isMe
                    ? LL().inbox.you()
                    : (getDisplayName(participant) ??
                      LL().comments.anonymousParticipant())}
                </span>
                <Show
                  when={!participant.isMe && participant.publicationUrl}
                  keyed
                >
                  {url => (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      class={participantPublicationUrlClass}
                    >
                      {url}
                    </a>
                  )}
                </Show>
              </div>
            </>
          )}
        </For>
      </div>
    )
  }

  return (
    <div class={participantsRowClass}>
      <div class={avatarStackClass}>
        <For each={visibleParticipants().slice(0, MAX_VISIBLE_AVATARS)}>
          {participant => (
            <img
              src={
                participant.profileImage
                  ? cdnClass(participant.profileImage, "smallThumb")
                  : getDefaultAvatar(participant.id)
              }
              alt={getDisplayName(participant) ?? ""}
              class={avatarClass}
            />
          )}
        </For>
        <Show when={overflowCount() > 0}>
          <div class={avatarOverflowClass}>+{overflowCount()}</div>
        </Show>
      </div>
      <div class={participantsNameClass}>{participantNames()}</div>
    </div>
  )
}
